import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material'
import {
    Visibility,
    Search,
    Refresh,
    HourglassEmpty,
    CheckCircle,
    Cancel,
    Person,
    Email,
    Download,
} from '@mui/icons-material'
import api from '@api/axios'
import { approvalApi } from '@api/endUser'
import { useAuthStore } from '@store/authStore'
import { useTranslation } from '@i18n'
import { exportApprovalsToExcel } from '@utils/exportUtils'

const PendingApprovals = () => {
    const navigate = useNavigate()
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const isRTL = language === 'ar'

    const [searchQuery, setSearchQuery] = useState('')
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const [exportLanguage, setExportLanguage] = useState(language)

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['pendingApprovals', user?.department_id],
        queryFn: () => approvalApi.getPendingApprovals(user?.department_id),
    })

    const { data: registrationCounts } = useQuery({
        queryKey: ['registrations-counts'],
        queryFn: async () => {
            const response = await api.get('/admin/registrations/counts')
            return response.data
        },
        enabled: user?.user_type === 'admin',
    })

    const approvals = data?.data || []

    // Filter approvals based on search
    const filteredApprovals = approvals.filter(approval => {
        if (!searchQuery) return true
        const query = searchQuery.toLowerCase()
        return (
            approval.ref_id?.toLowerCase().includes(query) ||
            approval.submitter_name?.toLowerCase().includes(query) ||
            approval.submitter_email?.toLowerCase().includes(query) ||
            approval.form_title?.toLowerCase().includes(query)
        )
    })

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <CheckCircle color="success" />
            case 'denied': return <Cancel color="error" />
            default: return <HourglassEmpty color="warning" />
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const handleExportToExcel = () => {
        if (!filteredApprovals || filteredApprovals.length === 0) {
            alert(isRTL ? 'لا توجد بيانات للتصدير' : 'No data to export')
            return
        }

        // Show language selection dialog
        setExportLanguage(language)
        setExportDialogOpen(true)
    }

    const handleConfirmExport = () => {
        exportApprovalsToExcel(filteredApprovals, exportLanguage)
        setExportDialogOpen(false)
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight={700}>
                    {isRTL ? 'الموافقات المعلقة' : 'Pending Approvals'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleExportToExcel}
                        disabled={isLoading || !filteredApprovals || filteredApprovals.length === 0}
                    >
                        {isRTL ? 'تصدير إلى Excel' : 'Export to Excel'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => refetch()}
                    >
                        {isRTL ? 'تحديث' : 'Refresh'}
                    </Button>
                </Box>
            </Box>

            {/* Info Alert */}
            {user?.user_type === 'admin' && registrationCounts?.pending > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => navigate('/registrations')}>
                            {isRTL ? 'الانتقال إلى التسجيلات' : 'Go to Registrations'}
                        </Button>
                    }
                >
                    {isRTL
                        ? `لديك ${registrationCounts.pending} طلب تسجيل مستخدم جديد بانتظار المراجعة.`
                        : `You have ${registrationCounts.pending} pending end-user account registration(s) awaiting review.`}
                </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
                {isRTL
                    ? 'هذه الصفحة تعرض الطلبات المقدمة من المستخدمين النهائيين والتي تحتاج إلى موافقتك.'
                    : 'This page shows submissions from end-users that require your approval.'}
            </Alert>

            {/* Search */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder={isRTL ? 'البحث بالمعرف أو اسم المقدم أو البريد الإلكتروني...' : 'Search by ID, submitter name, or email...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                    size="small"
                />
            </Paper>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 600 }}>
                                {isRTL ? 'معرف الطلب' : 'Submission ID'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                {isRTL ? 'النموذج' : 'Form'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                {isRTL ? 'مقدم الطلب' : 'Submitter'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                {isRTL ? 'تاريخ التقديم' : 'Submitted'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                {isRTL ? 'الحالة' : 'Status'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">
                                {isRTL ? 'الإجراءات' : 'Actions'}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredApprovals.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        {isRTL ? 'لا توجد موافقات معلقة' : 'No pending approvals'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredApprovals.map((approval) => (
                                <TableRow
                                    key={approval.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/submissions/${approval.submission_id}`)}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600} color="primary">
                                            {approval.ref_id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {isRTL ? (approval.form_title_ar || approval.form_title) : approval.form_title}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Person fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {approval.submitter_name || '-'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Email fontSize="small" color="action" />
                                                <Typography variant="body2" color="text.secondary">
                                                    {approval.submitter_email || '-'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(approval.submitted_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={getStatusIcon(approval.status)}
                                            label={
                                                approval.status === 'pending'
                                                    ? (isRTL ? 'قيد الانتظار' : 'Pending')
                                                    : approval.status === 'approved'
                                                        ? (isRTL ? 'موافق' : 'Approved')
                                                        : (isRTL ? 'مرفوض' : 'Denied')
                                            }
                                            color={
                                                approval.status === 'approved' ? 'success'
                                                    : approval.status === 'denied' ? 'error'
                                                        : 'warning'
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={isRTL ? 'عرض التفاصيل' : 'View Details'}>
                                            <IconButton
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`/submissions/${approval.submission_id}`)
                                                }}
                                            >
                                                <Visibility />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Export Language Selection Dialog */}
            <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Download color="primary" />
                    {isRTL ? 'اختر لغة التصدير' : 'Choose Export Language'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {isRTL
                            ? 'اختر اللغة التي تريد استخدامها لأسماء الأعمدة في ملف Excel'
                            : 'Choose the language for column headers in the Excel file'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: exportLanguage === 'en' ? 'primary.main' : 'divider',
                                bgcolor: exportLanguage === 'en' ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setExportLanguage('en')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: exportLanguage === 'en' ? 'primary.main' : 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {exportLanguage === 'en' && (
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography fontWeight={600}>English</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Column headers in English
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: exportLanguage === 'ar' ? 'primary.main' : 'divider',
                                bgcolor: exportLanguage === 'ar' ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setExportLanguage('ar')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: exportLanguage === 'ar' ? 'primary.main' : 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {exportLanguage === 'ar' && (
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography fontWeight={600}>العربية</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        أسماء الأعمدة بالعربية
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)}>
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={handleConfirmExport}
                    >
                        {isRTL ? 'تصدير' : 'Export'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default PendingApprovals
